import type { RouteKey, RoutePath } from '@elegant-router/types';
import type {
  LocationQueryRaw,
  NavigationGuardNext,
  RouteLocationNormalized,
  RouteLocationRaw,
  Router
} from 'vue-router';
import { getRouteName } from '@/router/elegant/transform';
import { getAuthorization } from '@/service/request/shared';
import { useAuthStore } from '@/store/modules/auth';
import { useRouteStore } from '@/store/modules/route';

/**
 * 创建路由守卫
 *
 * @param router router instance
 */
export function createRouteGuard(router: Router) {
  router.beforeEach(async (to, from, next) => {
    const location = await initRoute(to);

    if (location) {
      next(location);
      return;
    }

    const authStore = useAuthStore();

    // 定义各种路由拦截后跳转的routeKey
    const rootRoute: RouteKey = 'root';
    const loginRoute: RouteKey = 'login';
    const noAuthorizationRoute: RouteKey = '403';

    // 获取用户登录cookie
    const token = getAuthorization();
    const isLogin = Boolean(token);

    // 如果路由不是常量路由，则需要登录
    const needLogin = !to.meta.constant;
    // 获取访问路由所需角色
    const routeRoles = to.meta.roles || [];
    // 判断用户是否具有路由角色
    const hasRole = authStore.userInfo.role.some(role => routeRoles.includes(role));
    // 三种情况判断权限：1. 静态超级管理员 2. 路由没有角色限制 3. 用户具有路由角色
    const hasAuth = authStore.isStaticSuper || !routeRoles.length || hasRole;

    // 如果当前处于登录页面并且已经登录过就跳转首页
    if (to.name === loginRoute && isLogin) {
      next({ name: rootRoute });
      return;
    }

    // 如果路由不需要登录，则直接跳转
    if (!needLogin) {
      handleRouteSwitch(to, from, next);
      return;
    }

    // 如果路由需要登录但用户未登录，则跳转登录页面
    if (!isLogin) {
      next({ name: loginRoute, query: { redirect: to.fullPath } });
      return;
    }

    // 如果用户已登录但无权限，则跳转403页面
    if (!hasAuth) {
      next({ name: noAuthorizationRoute });
      return;
    }

    // 正常跳转路由
    handleRouteSwitch(to, from, next);
  });
}

/**
 * 初始化路由
 *
 * 该函数主要处理以下几种情况:
 *
 * 1. 常量路由未初始化时的处理
 * 2. 用户未登录时的路由访问控制
 * 3. 权限路由未初始化时的处理
 * 4. 404路由的特殊处理
 *
 * @param to 目标路由对象
 * @returns 返回重定向的路由信息,如果不需要重定向则返回null
 */
async function initRoute(to: RouteLocationNormalized): Promise<RouteLocationRaw | null> {
  const routeStore = useRouteStore();

  // 获取404路由的key和判断当前是否是404路由
  const notFoundRoute: RouteKey = 'not-found';
  const isNotFoundRoute = to.name === notFoundRoute;

  // 第一步: 处理常量路由未初始化的情况
  if (!routeStore.isInitConstantRoute) {
    // 初始化常量路由
    await routeStore.initConstantRoute();

    // 由于常量路由未初始化导致路由被404路由捕获
    // 在初始化完成后重定向到原始路由
    const path = to.fullPath;
    const location: RouteLocationRaw = {
      path,
      replace: true, // 替换当前路由历史记录
      query: to.query,
      hash: to.hash
    };

    return location;
  }

  // 第二步: 处理用户未登录的情况
  const token = getAuthorization();
  const isLogin = Boolean(token);

  if (!isLogin) {
    // 如果用户未登录,但访问的是常量路由(非404),则允许访问
    if (to.meta.constant && !isNotFoundRoute) {
      routeStore.onRouteSwitchWhenNotLoggedIn();
      return null;
    }

    // 如果用户未登录,则跳转到登录页面
    const loginRoute: RouteKey = 'login';
    const query = getRouteQueryOfLoginRoute(to, routeStore.routeHome);

    const location: RouteLocationRaw = {
      name: loginRoute,
      query
    };

    return location;
  }

  // 第三步: 处理权限路由未初始化的情况
  if (!routeStore.isInitAuthRoute) {
    // 初始化权限路由(此时也会获取用户信息)
    await routeStore.initAuthRoute();

    // 如果当前是404路由,说明是因为权限路由未初始化导致的
    // 在初始化完成后需要重定向到原始路由
    if (isNotFoundRoute) {
      const rootRoute: RouteKey = 'root';
      // 如果是从根路由重定向来的,则跳转到根路径'/',否则跳转到完整路径
      const path = to.redirectedFrom?.name === rootRoute ? '/' : to.fullPath;

      const location: RouteLocationRaw = {
        path,
        replace: true,
        query: to.query,
        hash: to.hash
      };

      return location;
    }
  }

  // 在每次路由切换的时候都会请求用户信息
  routeStore?.onRouteSwitchWhenLoggedIn();

  // 第四步: 处理404路由的特殊情况
  // 如果不是404路由,则允许访问
  if (!isNotFoundRoute) {
    return null;
  }

  // 如果是404路由,则检查该路由是否存在于权限路由中
  const exist = await routeStore.getIsAuthRouteExist(to.path as RoutePath);
  const noPermissionRoute: RouteKey = '403';

  // 如果路由存在但无权访问,则跳转到403页面
  if (exist) {
    const location: RouteLocationRaw = {
      name: noPermissionRoute
    };

    return location;
  }

  // 如果路由不存在,则保持404状态
  return null;
}

function handleRouteSwitch(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
  // route with href
  if (to.meta.href) {
    window.open(to.meta.href, '_blank');

    next({ path: from.fullPath, replace: true, query: from.query, hash: to.hash });

    return;
  }

  next();
}

/**
 * 获取登录路由的查询参数
 *
 * 1. 当用户访问需要登录的页面时,会被重定向到登录页面
 * 2. 登录成功后需要跳转回原来要访问的页面
 * 3. 该函数用于处理重定向相关的查询参数
 *
 * @example
 *   1. 如果目标页面不是登录页且不是首页,则将完整路径作为redirect参数
 *   2. 如果目标页面是首页且有查询参数,则将查询参数拼接到首页路径
 *
 * @param to - 目标路由对象
 * @param routeHome - 首页路由的key
 * @returns 登录路由的查询参数对象
 */
function getRouteQueryOfLoginRoute(to: RouteLocationNormalized, routeHome: RouteKey) {
  // 登录页路由key
  const loginRoute: RouteKey = 'login';
  // 获取完整的目标路径
  const redirect = to.fullPath;
  // 分割路径和查询参数
  const [redirectPath, redirectQuery] = redirect.split('?');
  // 获取目标路径对应的路由名称
  const redirectName = getRouteName(redirectPath as RoutePath);

  // 判断是否重定向到首页
  const isRedirectHome = routeHome === redirectName;

  // 构造查询参数对象:
  // - 如果目标页面不是登录页且不是首页,则将完整路径作为redirect参数
  // - 否则返回空对象
  const query: LocationQueryRaw = to.name !== loginRoute && !isRedirectHome ? { redirect } : {};

  // 如果重定向到首页且有查询参数,则将查询参数拼接到首页路径
  if (isRedirectHome && redirectQuery) {
    query.redirect = `/?${redirectQuery}`;
  }

  return query;
}
